import React from "react";
import store from '@/store';
import { isWebComponentEnvironment, isRightMouseButtonClicked } from '@/helpers';
import { setFloatableInputVisible } from '@/actions';

const webComponentEnvironment = isWebComponentEnvironment();

export const clickableClassName = 'clickable';
export const draggableClassName = 'draggable';
export const clickTimeoutDuration = 200; // ms
export const dblSecondClickTimeoutDuration = 200; // ms

const activeElementsSelector = `.${clickableClassName}, .${draggableClassName}`;

const ACTION_START_EL_SVG_NODE = 'svgNode';
const ACTION_START_EL_SVG_NOT_ACTIVE_CHILD = 'svgChild';

const ACTION_START = 1;
const ACTION_MOVE = 2;
const ACTION_END = 3;
const ACTION_EMERGENCY_STOP = 4;

const SWIPE_MIN_LENGTH_PX = 100;
/**
 * provide events:
 * - onDrag
 * - onDragEnd
 * - onSwipe
 * - onFastClickTap
 * - onDblClickTap
 * - onZooming
 * - onZoomingEnd
 **/
class InteractiveSvg extends React.Component {
    constructor(props) {
        super(props);

        this.svgNode = null;

        this.startX = 0;
        this.startY = 0;
        this.dx = 0;
        this.dy = 0;
        this.zoomingDistance = 0;
        this.actionStartAt = 0;

        this.isDblSecondClick = false;
        this.isDraggableStarted = false;
        this.actionStartEl = null;
        this.svgViewboxStart = [];
        this.isMultitouch = false;
        this.zoomingProcess = {};

        this.onActionStart = this.onActionStart.bind(this);
        this.onActionMove = this.onActionMove.bind(this);
        this.onActionEnd = this.onActionEnd.bind(this);
    }

    setSvgRef(svgNode) {
        if (!this.svgNode && svgNode) {
            this.svgNode = svgNode;
            this.addSubscriptions();
        }
    }

    addSubscriptions() {
        this.svgNode.addEventListener('touchstart', this.onActionStart);
        this.svgNode.addEventListener('touchmove', this.onActionMove);
        this.svgNode.addEventListener('touchend', this.onActionEnd);
        this.svgNode.addEventListener('touchcancel', this.onActionEnd);
        this.svgNode.addEventListener('mousedown', this.onActionStart);
        this.svgNode.addEventListener('mousemove', this.onActionMove);
        this.svgNode.addEventListener('mouseup', this.onActionEnd);
        this.svgNode.addEventListener('mouseleave', this.onActionEnd);
    }

    onActionStart(e) {
        e.preventDefault();

        this.isMultitouch = this.isMultitouch || (this.getTouchesCount(e) > 1);

        if(!this.isMultitouch) {
            clearTimeout(this.clickWaitingTimeout);
            this.clickWaitingTimeout = setTimeout(() => {
                this.clickWaitingTimeout = null;
            }, clickTimeoutDuration);

            this.actionStartAt = new Date().getTime();

            const ePos = this.getEventPosition(e);
            this.startX = ePos.x;
            this.startY = ePos.y;

            const activeElem = this.isActiveElem(e.target) ? e.target : this.getParentActiveElem(e.target);
            if (activeElem) {
                this.actionStartEl = activeElem;
            } else if (e.target === this.svgNode) {
                // can be swipe
                this.actionStartEl = ACTION_START_EL_SVG_NODE;
            } else {
                // can be drag
                this.actionStartEl = ACTION_START_EL_SVG_NOT_ACTIVE_CHILD;
            }

            if (webComponentEnvironment) {
                if (isRightMouseButtonClicked(e)) {
                    const { core: { floatableInputVisible: isEditModeEnabled } } = store.getState();
                    if (isEditModeEnabled) {
                        store.dispatch(setFloatableInputVisible(false));
                    }
                } else {
                    if  (this.actionStartEl === ACTION_START_EL_SVG_NOT_ACTIVE_CHILD) {
                        return;
                    }
                }
            }

            this.checkClicking(e, ACTION_START);
            this.checkDragging(e, ACTION_START);
        } else {
            if (this.actionStartEl) {
                this.checkClicking(e, ACTION_EMERGENCY_STOP);
                this.checkDragging(e, ACTION_EMERGENCY_STOP);
            }
            this.actionStartEl = null;

            this.checkZooming(e, ACTION_START);
        }
    }

    onActionMove(e) {
        e.preventDefault();

        if(!this.isMultitouch) {
            if (this.actionStartEl && this.clickWaitingTimeout === null) {
                if (webComponentEnvironment) {
                    if (isRightMouseButtonClicked(e)) {
                        if  (this.actionStartEl !== ACTION_START_EL_SVG_NOT_ACTIVE_CHILD) {
                            return;
                        } 
                    } else {
                        if  (this.actionStartEl === ACTION_START_EL_SVG_NOT_ACTIVE_CHILD) {
                            return;
                        } else if (this.isClickable(this.actionStartEl) && !this.isDraggable(this.actionStartEl)) {
                            return;
                        }
                    }
                }

                const ePos = this.getEventPosition(e);
                this.dx = ePos.x - this.startX;
                this.dy = ePos.y - this.startY;

                this.checkClicking(e, ACTION_MOVE);
                this.checkDragging(e, ACTION_MOVE);
            }
        } else {
            this.checkZooming(e, ACTION_MOVE);
        }
    }

    onActionEnd(e) {
        if (this.clickWaitingTimeout !== null) {
            clearTimeout(this.clickWaitingTimeout);
            this.clickWaitingTimeout = null;
        }

        e.preventDefault();

        if(this.isMultitouch) {
            this.isMultitouch = this.getTouchesCount(e) > 0;
        }

        if (this.actionStartEl) {
            const ePos = this.getEventPosition(e);
            this.dx = ePos.x - this.startX;
            this.dy = ePos.y - this.startY;

            this.checkSwiping(e, ACTION_END) || this.checkDragging(e, ACTION_END) || this.checkClicking(e, ACTION_END);
        }

        this.checkZooming(e, ACTION_END);

        this.actionStartEl = null;
    }

    checkClicking(e, action) {
        let isAccepted = false;
        if (action === ACTION_START) {
            let clickableActElem = null;
            if (this.actionStartEl && !this.isConstant(this.actionStartEl)) {
                clickableActElem = this.isClickable(this.actionStartEl) ? this.actionStartEl : null;
                if (clickableActElem) {
                    this.dispatchEvent('onActionStart', clickableActElem, this.toEventData(e, clickableActElem));
                    isAccepted = true;
                }
            }
            this.isDblSecondClick = this.dblClickCheckFinish(clickableActElem);
        } else if (action === ACTION_MOVE) {
            this.isDblSecondClick = false;
        } else if (action === ACTION_END) {
            const clickableActElem = this.isClickable(this.actionStartEl) ? this.actionStartEl : null;
            if (this.isDblSecondClick) {
                this.isDblSecondClick = false;
                if (clickableActElem) {
                    this.dispatchEvent('onDblClickTap', clickableActElem, this.toEventData(e, clickableActElem));
                }
                this.dispatchBaseDataEventCallback('onDblClickTap', e, clickableActElem);
            } else {
                this.dblClickCheckStart(clickableActElem);
                if (clickableActElem) {
                    this.dispatchEvent('onFastClickTap', clickableActElem, this.toEventData(e, clickableActElem));
                }
                this.dispatchBaseDataEventCallback('onFastClickTap', e, clickableActElem);
            }
            isAccepted = true;
        } else if (action === ACTION_EMERGENCY_STOP) {
            if (this.isClickable(this.actionStartEl)) {
                this.dispatchEvent('onEmergencyStop', this.actionStartEl, this.toEventData(e, this.actionStartEl));
                isAccepted = true;
            }
            this.isDblSecondClick = false;
        }
        return isAccepted;
    }

    checkDragging(e, action) {
        let isAccepted = false;
        if (action === ACTION_START) {
            this.isDraggableStarted = false;
            if (this.actionStartEl === ACTION_START_EL_SVG_NOT_ACTIVE_CHILD) {
                this.svgViewboxStart = this.svgNode.getAttribute('viewBox').split(' ');
            }
        } else if (action === ACTION_MOVE) {
            if (this.actionStartEl !== ACTION_START_EL_SVG_NODE) {
                if (!this.isDraggableStarted && this.actionStartEl !== ACTION_START_EL_SVG_NOT_ACTIVE_CHILD && !this.isDraggable(this.actionStartEl)) {
                    this.actionStartEl = ACTION_START_EL_SVG_NOT_ACTIVE_CHILD;
                }
                if (this.actionStartEl !== ACTION_START_EL_SVG_NOT_ACTIVE_CHILD) {
                    this.isDraggableStarted = true;
                    this.dispatchEvent('onDrag', this.actionStartEl, this.toEventData(e, this.actionStartEl));
                    isAccepted = true;
                } else if (this.props.draggableContent) {
                    if (!this.isDraggableStarted) {
                        this.svgViewboxStart = this.svgNode.getAttribute('viewBox').split(' ');
                    }
                    this.isDraggableStarted = true;
                    this.dispatchBaseDataEventCallback('onDrag', e);
                    isAccepted = true;
                    if (this.svgViewboxStart && this.svgViewboxStart.length) {
                        this.svgNode.setAttribute(
                            'viewBox',
                            `${this.svgViewboxStart[0] - this.dx} ${this.svgViewboxStart[1] - this.dy} ${this.svgViewboxStart[2]} ${this.svgViewboxStart[3]}`,
                        );
                    }
                }
            }
        } else if (action === ACTION_END || action === ACTION_EMERGENCY_STOP) {
            if (this.isDraggableStarted) {
                if (this.actionStartEl !== ACTION_START_EL_SVG_NOT_ACTIVE_CHILD) {
                    this.dispatchEvent('onDragEnd', this.actionStartEl, this.toEventData(e, this.actionStartEl));
                } else if (this.props.draggableContent) {
                    this.dispatchBaseDataEventCallback('onDragEnd', e);
                }
                isAccepted = true;
            }
            this.isDraggableStarted = false;
        }
        return isAccepted;
    }

    checkSwiping(e, action) {
        let isAccepted = false;
        if (action === ACTION_END) {
            if (this.actionStartEl === ACTION_START_EL_SVG_NODE) {
                const direction = (Math.abs(this.dx) < SWIPE_MIN_LENGTH_PX)
                    ? 0
                    : (this.dx > 0 ? 1 : -1);
                if (direction) {
                    this.dispatchEventCallback('onSwipe', {
                        actionStartAt: this.actionStartAt,
                        originEvent: e,
                        direction: direction
                    });
                    isAccepted = true;
                }
            }
        }
        return isAccepted;
    }

    checkZooming(e, action) {
        const oldZoomingDistance = this.zoomingDistance;
        this.zoomingDistance = 0;
        let execEnd = false;
        if (this.props.zoomingContent && this.props.onZooming) {
            if (action === ACTION_START) {
                if (this.getTouchesCount(e) === 2) {
                    const svgRect = this.svgNode.getBoundingClientRect();
                    this.zoomingProcess = {
                        startTouchesDist: Math.hypot(
                            (e.touches[0].clientX - e.touches[1].clientX),
                            (e.touches[0].clientY - e.touches[1].clientY),
                        ),
                        startTouchesCenterPos: {
                            x: e.touches[0].clientX - svgRect.x - (e.touches[0].clientX - e.touches[1].clientX) / 2,
                            y: e.touches[0].clientY - svgRect.y - (e.touches[0].clientY - e.touches[1].clientY) / 2
                        }
                    };
                } else {
                    execEnd = true;
                }
            } else if (action === ACTION_MOVE) {
                if (this.zoomingProcess.hasOwnProperty('startTouchesDist')) {
                    if (!this.zoomingProcess.inMoveCalcProgress) { // skip events if calculation is complicated
                        this.zoomingProcess.inMoveCalcProgress = true;
                        const newTouchesDist = Math.hypot(
                            (e.touches[0].clientX - e.touches[1].clientX),
                            (e.touches[0].clientY - e.touches[1].clientY),
                        );
                        const newZoomingDistance = Math.trunc(newTouchesDist - this.zoomingProcess.startTouchesDist);
                        if (newZoomingDistance !== oldZoomingDistance) {
                            this.zoomingDistance = newZoomingDistance;
                            this.dispatchBaseDataEventCallback('onZooming', e);
                            this.zoomingProcess.onZoomingSent = true;
                        }
                        this.zoomingProcess.inMoveCalcProgress = false;
                    }
                }
            } else if (action === ACTION_END) {
                execEnd = true;
            }
        } else {
            execEnd = true;
        }
        if (execEnd) {
            if (this.zoomingProcess.onZoomingSent) {
                this.dispatchBaseDataEventCallback('onZoomingEnd', e);
            }
            this.zoomingProcess = {};
        }
    }

    dblClickCheckStart(clickableActElem) {
        clearTimeout(this.doubleClickClearTimeout);
        const compareEl = clickableActElem || ACTION_START_EL_SVG_NODE;
        this.doubleClickFirstEl = compareEl;
        this.doubleClickClearTimeout = setTimeout(() => {
            this.doubleClickFirstEl = null;
            this.doubleClickClearTimeout = null;
        }, dblSecondClickTimeoutDuration);
    }

    dblClickCheckFinish(clickableActElem) {
        clearTimeout(this.doubleClickClearTimeout);
        const compareEl = clickableActElem || ACTION_START_EL_SVG_NODE;
        const res = this.doubleClickFirstEl === compareEl;
        this.doubleClickFirstEl = null;
        return res;
    }

    isConstant(el) {
        return typeof(el) === 'string';
    }

    isDraggable(node) {
        return node && !this.isConstant(node) && node.classList && node.classList.contains(draggableClassName);
    }

    isClickable(node) {
        return node && !this.isConstant(node) && node.classList && node.classList.contains(clickableClassName);
    }

    isActiveElem(node) {
        return this.isDraggable(node) || this.isClickable(node);
    };

    getParentActiveElem(node) {
        return node.closest(activeElementsSelector);
    };

    getTouchesCount(e) {
        return e.touches?.length || 0;
    }

    getEventPosition(e) {
        const eX = e.clientX || (e.touches[0] || e.changedTouches[0]).clientX;
        const eY = e.clientY || (e.touches[0] || e.changedTouches[0]).clientY;
        const svgRect = this.svgNode.getBoundingClientRect();
        return {
            x: (eX - svgRect.x),
            y: (eY - svgRect.y)
        };
    }

    dispatchEvent(eventName, elem, eventDetail) {
        elem.dispatchEvent(new CustomEvent(eventName, { detail: eventDetail }));
    }

    dispatchBaseDataEventCallback(eventName, originEvent, activeElem) {
        this.dispatchEventCallback(eventName, this.toEventData(originEvent, activeElem));
    }

    dispatchEventCallback(eventName, eventData) {
        if (typeof(this.props[eventName]) === 'function') {
            try {
                this.props[eventName](eventData);
            } catch (ex) {
                console.error(ex);
            }
        }
    }

    toEventData(originEvent, activeElem) {
        return {
            startX: this.startX,
            startY: this.startY,
            dx: this.dx,
            dy: this.dy,
            zoomingDistance: this.zoomingDistance,
            zoomingStartCenter: this.zoomingProcess?.startTouchesCenterPos,
            actionStartAt: this.actionStartAt,
            activeElem: activeElem || null,
            target: originEvent ? originEvent.target : null,
            originEvent: originEvent
        }
    }

    removeSubscriptions() {
        this.svgNode.removeEventListener('touchstart', this.onActionStart);
        this.svgNode.removeEventListener('touchmove', this.onActionMove);
        this.svgNode.removeEventListener('touchend', this.onActionEnd);
        this.svgNode.removeEventListener('touchcancel', this.onActionEnd);
        this.svgNode.removeEventListener('mousedown', this.onActionStart);
        this.svgNode.removeEventListener('mousemove', this.onActionMove);
        this.svgNode.removeEventListener('mouseup', this.onActionEnd);
        this.svgNode.removeEventListener('mouseleave', this.onActionEnd);
    }

    componentWillUnmount() {
        if (this.svgNode) {
            this.removeSubscriptions();
        }
    }

    render() {
        const {zoomingContent, draggableContent, onDrag, onDragEnd, onSwipe, onFastClickTap, onDblClickTap, onZooming, onZoomingEnd, ...otherProps} = this.props;
        return (
            <svg {...otherProps} ref={elem => this.setSvgRef(elem)}>
                {this.props.children}
            </svg>
        );
    }
}

export default InteractiveSvg;
